import { anchorHold, CALM_EXTRA_REST, CALM_PARTS, COUNT_IN_BARS, dressNotes, KIT_POOL, levelSpec, STAB_BEATS } from './chart';
import type { FormPart, LevelSpec, Slot, Song } from './chart';
import { createRng } from './rng';
import type { Rng } from './rng';
import type { Beat, SectionName } from './types';
import { BEATS_PER_BAR } from '../audio/plan';

export type AccentKind = 'drop' | '808' | 'kick' | 'snare' | 'stab' | 'melody';

export type Accent = { beat: number; slot: number; kind: AccentKind; strength: number };

export type TrackPart = { name: string; bar: number; bars: number };

export type TrackCalendar = { bpm: number; lead: number; beats: number; form: TrackPart[]; accents: Accent[] };

export type TrackSong = Song & { readonly lead: number; readonly pulse: number };

export const ACCENT_RANK: Readonly<Record<AccentKind, number>> = { drop: 0, '808': 1, kick: 2, snare: 3, stab: 4, melody: 5 };

export const HALF_TIME_ABOVE = 140;

const BASE_KINDS: readonly AccentKind[] = ['drop', 'kick', 'snare'];
const EPSILON = 1e-9;

export type Pick = { kinds: readonly AccentKind[]; slots: number; strength: number };

export type FaseOverrides = Partial<Omit<LevelSpec, 'level'>>;

export const specOf = (level: number | LevelSpec): LevelSpec => (typeof level === 'number' ? levelSpec(level) : level);

export function faseSpec(level: number, overrides: FaseOverrides = {}): LevelSpec {
  return { ...levelSpec(level), ...overrides };
}

export function pickOf(level: number | LevelSpec): Pick {
  const spec = specOf(level);
  const kinds: AccentKind[] = [...BASE_KINDS];
  if (spec.level >= 3) kinds.push('808', 'stab');
  if (spec.level >= 5) kinds.push('melody');
  const slots = spec.level >= 8 ? 1 : spec.level >= 6 ? 2 : spec.grid * 4;
  const strength = Math.max(0.3, 0.85 - 0.1 * (spec.level - 1));
  return { kinds, slots, strength };
}

export function sectionOf(name: string): SectionName {
  const n = name.toLowerCase();
  if (n.startsWith('intro')) return 'intro';
  if (n.startsWith('outro')) return 'outro';
  if (n.startsWith('break')) return 'break';
  if (n.startsWith('build') || n.startsWith('rise')) return 'build';
  if (n.startsWith('drop') || n.startsWith('peak') || n.startsWith('hook') || n.startsWith('boss')) return 'peak';
  return 'groove';
}

export function pulseOf(bpm: number): number {
  return bpm > HALF_TIME_ABOVE ? 2 : 1;
}

function partOf(form: readonly FormPart[], beat: Beat): SectionName {
  let name: SectionName = form[0]?.name ?? 'intro';
  for (const part of form) if (part.bar * BEATS_PER_BAR <= beat + EPSILON) name = part.name;
  return name;
}

function holdOf(rng: Rng, spec: LevelSpec): Beat {
  const top = anchorHold(spec);
  const pool = spec.holds.length > 1 ? spec.holds.filter((h) => h < top) : spec.holds;
  return spec.stabOdds > 0 && rng.chance(spec.stabOdds) ? STAB_BEATS : rng.pick(pool);
}

export function chartFromTrack(seed: string, calendar: TrackCalendar, level: number | LevelSpec): TrackSong {
  const spec = specOf(level);
  const pulse = pulseOf(calendar.bpm);
  const pick = pickOf(spec);
  const kit = createRng(seed).fork('kit').pick(KIT_POOL);
  const rng = createRng(seed).fork(`t${String(spec.level)}`);
  const form: FormPart[] = calendar.form.map((p) => ({ name: sectionOf(p.name), bar: p.bar / pulse, bars: p.bars / pulse }));
  const endBeat = calendar.beats / pulse;
  const last = endBeat - BEATS_PER_BAR;
  const apexes = calendar.accents
    .filter((a) => pick.kinds.includes(a.kind) && a.strength >= pick.strength - EPSILON && a.slot % (pick.slots * pulse) === 0)
    .map((a) => ({ beat: a.beat / pulse, kind: a.kind }))
    .filter((a) => a.beat <= last + EPSILON);
  const top = anchorHold(spec);
  const rest = (apex: Beat) => spec.rest + (CALM_PARTS.includes(partOf(form, apex)) ? CALM_EXTRA_REST : 0);
  const next = (from: Beat, limit: Beat) => apexes.find((a) => a.beat >= from - EPSILON && a.beat <= limit + EPSILON);
  const anchor = apexes.find((a) => a.kind === 'drop' && a.beat >= COUNT_IN_BARS * BEATS_PER_BAR + top - EPSILON);

  const slots: Slot[] = [];
  const fill = (from: Beat, limit: Beat): void => {
    let cursor = from;
    for (;;) {
      const hold = holdOf(rng, spec);
      const at = next(cursor + hold, limit);
      if (at === undefined) return;
      slots.push({ hold, apex: at.beat });
      cursor = at.beat + rest(at.beat);
    }
  };
  const start = COUNT_IN_BARS * BEATS_PER_BAR;
  if (anchor === undefined) fill(start, last);
  else {
    fill(start, anchor.beat - top - spec.rest);
    slots.push({ hold: top, apex: anchor.beat });
    fill(anchor.beat + rest(anchor.beat), last);
  }

  const notes = dressNotes(rng, spec, slots, (beat) => partOf(form, beat));
  return { seed, level: spec.level, bpm: calendar.bpm / pulse, kit, form, notes, endBeat, lead: calendar.lead, pulse };
}
